import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FileText, Download, LayoutGrid, List, History, Share2, ChevronDown, CalendarDays, AlertCircle } from 'lucide-react';
import { reportsAPI } from '../lib/api';
import { useOrg } from '../contexts/OrgContext';
import { useAuth } from '../contexts/AuthContext';
import ReportingAssistant from '../components/ai/ReportingAssistant';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

import { ProfitLossReport } from '../components/reports/ProfitLossReport';
import { CashFlowReport } from '../components/reports/CashFlowReport';
import { VATReport } from '../components/reports/VATReport';
import { ExpenseReport } from '../components/reports/ExpenseReport';
import { TrialBalanceReport } from '../components/reports/TrialBalanceReport';
import { BalanceSheetReport } from '../components/reports/BalanceSheetReport';
import { GeneralLedgerReport } from '../components/reports/GeneralLedgerReport';
import { OverviewSection } from '../components/reports/OverviewSection';
import { HeartbeatSection } from '../components/reports/HeartbeatSection';

export default function ReportsPage() {
  const { isPersonal, org } = useOrg();
  const { user } = useAuth();
  const entityName = org?.company_name || user?.name || '';
  const [activeReport, setActiveReport] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [healthData, setHealthData] = useState(null);
  const [heartbeatData, setHeartbeatData] = useState(null);
  const [viewMode, setViewMode] = useState('visual');
  const [filters, setFilters] = useState({
    start_date: '', end_date: '',
    as_of_date: new Date().toISOString().split('T')[0]
  });
  const [includePY, setIncludePY] = useState(false);
  const [showPresets, setShowPresets] = useState(false);
  const [integrityData, setIntegrityData] = useState(null);

  // Close presets dropdown when clicking outside
  useEffect(() => {
    if (!showPresets) return;
    const handler = (e) => {
      if (!e.target.closest('[data-testid="date-presets-btn"]') && !e.target.closest('[data-testid="as-of-presets-btn"]') && !e.target.closest('[data-testid^="preset-"]')) {
        setShowPresets(false);
      }
    };
    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, [showPresets]);

  // ─── Date Presets ──────────────────────────────────────
  const getDatePresets = () => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    const today = now.toISOString().split('T')[0];
    const fmt = (d) => d.toISOString().split('T')[0];
    const firstOfMonth = (yr, mo) => new Date(yr, mo, 1);
    const lastOfMonth = (yr, mo) => new Date(yr, mo + 1, 0);
    const quarterStart = (yr, q) => new Date(yr, q * 3, 1);
    const quarterEnd = (yr, q) => lastOfMonth(yr, q * 3 + 2);
    const currentQ = Math.floor(m / 3);
    const prevQ = currentQ === 0 ? 3 : currentQ - 1;
    const prevQYear = currentQ === 0 ? y - 1 : y;

    return {
      range: [
        { label: 'This Month', start: fmt(firstOfMonth(y, m)), end: today },
        { label: 'Last Month', start: fmt(firstOfMonth(m === 0 ? y - 1 : y, m === 0 ? 11 : m - 1)), end: fmt(lastOfMonth(m === 0 ? y - 1 : y, m === 0 ? 11 : m - 1)) },
        { label: `This Quarter (Q${currentQ + 1})`, start: fmt(quarterStart(y, currentQ)), end: today },
        { label: `Last Quarter (Q${prevQ + 1})`, start: fmt(quarterStart(prevQYear, prevQ)), end: fmt(quarterEnd(prevQYear, prevQ)) },
        { label: 'Year to Date', start: `${y}-01-01`, end: today },
        { label: `Last Year (${y - 1})`, start: `${y - 1}-01-01`, end: `${y - 1}-12-31` },
        { label: 'This Month Last Year', start: fmt(firstOfMonth(y - 1, m)), end: fmt(lastOfMonth(y - 1, m)) },
        { label: 'Rolling 12 Months', start: fmt(new Date(y - 1, m, now.getDate())), end: today },
        { label: 'Rolling 6 Months', start: fmt(new Date(y, m - 6, now.getDate())), end: today },
      ],
      asOf: [
        { label: 'Today', date: today },
        { label: 'End of Last Month', date: fmt(lastOfMonth(m === 0 ? y - 1 : y, m === 0 ? 11 : m - 1)) },
        { label: 'End of Last Quarter', date: fmt(quarterEnd(prevQYear, prevQ)) },
        { label: `End of Last Year (${y - 1})`, date: `${y - 1}-12-31` },
      ],
    };
  };

  const apiMap = useMemo(() => ({
    'trial-balance': reportsAPI.getTrialBalance,
    'profit-loss': reportsAPI.getProfitLoss,
    'balance-sheet': reportsAPI.getBalanceSheet,
    'general-ledger': reportsAPI.getGeneralLedger,
    'cash-flow': reportsAPI.getCashFlow,
    'vat': reportsAPI.getVAT,
    'expenses': reportsAPI.getExpenseAnalysis,
  }), []);

  const applyRangePreset = (preset) => {
    setFilters(prev => ({ ...prev, start_date: preset.start, end_date: preset.end }));
    setLoading(true);
    const params = { start_date: preset.start, end_date: preset.end };
    if (['profit-loss', 'balance-sheet'].includes(activeReport) && includePY) params.include_py = true;
    apiMap[activeReport]?.(params).then(res => setReportData(res.data)).catch(() => toast.error('Failed to load report')).finally(() => setLoading(false));
  };

  const applyAsOfPreset = (preset) => {
    setFilters(p => ({ ...p, as_of_date: preset.date }));
    setLoading(true);
    const params = { as_of_date: preset.date };
    if (['profit-loss', 'balance-sheet'].includes(activeReport) && includePY) params.include_py = true;
    apiMap[activeReport]?.(params).then(res => setReportData(res.data)).catch(() => toast.error('Failed to load report')).finally(() => setLoading(false));
  };

  const loadIntegrityCheck = async () => {
    try {
      const res = await reportsAPI.getIntegrityCheck();
      setIntegrityData(res.data);
    } catch (err) {
      console.warn('Integrity check failed:', err);
    }
  };

  const loadHealthData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await reportsAPI.getFinancialHealth();
      setHealthData(res.data);
    } catch {
      toast.error('Failed to load financial health');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadHeartbeatData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await reportsAPI.getHeartbeat();
      setHeartbeatData(res.data);
    } catch {
      toast.error('Failed to load heartbeat');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadReport = useCallback(async (reportType) => {
    setLoading(true);
    try {
      const params = {};
      if (['trial-balance', 'balance-sheet'].includes(reportType)) {
        if (filters.as_of_date) params.as_of_date = filters.as_of_date;
      } else {
        if (filters.start_date) params.start_date = filters.start_date;
        if (filters.end_date) params.end_date = filters.end_date;
      }
      if (['profit-loss', 'balance-sheet'].includes(reportType) && includePY) params.include_py = true;
      const res = await apiMap[reportType](params);
      setReportData(res.data);
    } catch {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [filters, includePY, apiMap]);

  useEffect(() => {
    if (activeReport === 'overview') {
      loadHealthData();
      loadIntegrityCheck();
    } else if (activeReport === 'heartbeat') {
      loadHeartbeatData();
    } else {
      setReportData(null);
      loadReport(activeReport);
    }
  }, [activeReport, loadHealthData, loadHeartbeatData, loadReport]);

  const PDF_SUPPORTED = ['profit-loss', 'balance-sheet', 'trial-balance', 'general-ledger'];
  const CSV_SUPPORTED = ['profit-loss', 'balance-sheet', 'trial-balance', 'general-ledger'];

  const handleExport = async (format) => {
    const params = { report_type: activeReport };
    if (['trial-balance', 'balance-sheet'].includes(activeReport)) {
      if (filters.as_of_date) params.as_of_date = filters.as_of_date;
    } else {
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
    }
    if (['profit-loss', 'balance-sheet'].includes(activeReport) && includePY) params.include_py = true;

    try {
      if (format === 'csv') {
        const { data } = await reportsAPI.exportRaw('/reports/export/csv', params, 'blob');
        const a = document.createElement('a');
        a.href = URL.createObjectURL(data);
        a.download = `${activeReport}_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
      } else {
        const { data } = await reportsAPI.exportRaw('/reports/export/pdf', params, 'text');
        const blob = new Blob([data], { type: 'text/html; charset=utf-8' });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 5000);
      }
      toast.success(`${format.toUpperCase()} exported`);
    } catch (err) {
      let detail = err.message || 'Unknown error';
      if (err.response?.data instanceof Blob) {
        try { detail = JSON.parse(await err.response.data.text()).detail; } catch {}
      } else if (err.response?.data?.detail) {
        detail = err.response.data.detail;
      }
      toast.error(`Export failed: ${detail}`);
    }
  };

  const handleShare = async () => {
    if (!navigator.share) { toast.error('Sharing is not supported on this browser'); return; }
    const params = { report_type: activeReport };
    if (['trial-balance', 'balance-sheet'].includes(activeReport)) {
      if (filters.as_of_date) params.as_of_date = filters.as_of_date;
    } else {
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
    }
    if (['profit-loss', 'balance-sheet'].includes(activeReport) && includePY) params.include_py = true;
    const reportLabel = activeReport.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    try {
      const { data: blob } = await reportsAPI.exportRaw('/reports/export/csv', params, 'blob');
      const fileName = `${reportLabel.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
      const file = new File([blob], fileName, { type: 'text/csv' });
      await navigator.share({ title: `${reportLabel} — kaya.ai`, text: `Here is the ${reportLabel} report from kaya.ai`, files: [file] });
      toast.success('Report shared');
    } catch (err) {
      if (err.name !== 'AbortError') {
        try {
          await navigator.share({ title: `${reportLabel} — kaya.ai`, text: `Check out the ${reportLabel} report on kaya.ai`, url: window.location.href });
          toast.success('Link shared');
        } catch {
          toast.error('Sharing cancelled');
        }
      }
    }
  };

  const needsDateRange = ['profit-loss', 'general-ledger', 'cash-flow', 'vat', 'expenses'].includes(activeReport);
  const needsAsOfDate = ['trial-balance', 'balance-sheet'].includes(activeReport);
  const canShowPY = ['profit-loss', 'balance-sheet'].includes(activeReport);
  const isDetailReport = activeReport !== 'overview' && activeReport !== 'heartbeat';

  const renderActiveReport = () => {
    switch (activeReport) {
      case 'profit-loss': return <ProfitLossReport reportData={reportData} viewMode={viewMode} entityName={entityName} includePY={includePY} />;
      case 'cash-flow': return <CashFlowReport reportData={reportData} viewMode={viewMode} entityName={entityName} />;
      case 'vat': return <VATReport reportData={reportData} viewMode={viewMode} entityName={entityName} />;
      case 'expenses': return <ExpenseReport reportData={reportData} viewMode={viewMode} entityName={entityName} />;
      case 'trial-balance': return <TrialBalanceReport reportData={reportData} viewMode={viewMode} entityName={entityName} />;
      case 'balance-sheet': return <BalanceSheetReport reportData={reportData} viewMode={viewMode} entityName={entityName} includePY={includePY} />;
      case 'general-ledger': return <GeneralLedgerReport reportData={reportData} viewMode={viewMode} entityName={entityName} />;
      default: return null;
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      <Tabs value={activeReport} onValueChange={setActiveReport}>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row justify-between gap-4">
            <TabsList className="bg-zinc-100 inline-flex h-auto gap-0.5 p-1 w-auto overflow-x-auto max-w-full no-scrollbar">
              <TabsTrigger value="overview" className="data-[state=active]:bg-white text-xs sm:text-sm" data-testid="tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="heartbeat" className="data-[state=active]:bg-white text-xs sm:text-sm" data-testid="tab-heartbeat">Heartbeat</TabsTrigger>
              <TabsTrigger value="profit-loss" className="data-[state=active]:bg-white text-xs sm:text-sm" data-testid="tab-profit-loss">P&L</TabsTrigger>
              <TabsTrigger value="cash-flow" className="data-[state=active]:bg-white text-xs sm:text-sm" data-testid="tab-cash-flow">Cash Flow</TabsTrigger>
              {!isPersonal && <TabsTrigger value="vat" className="data-[state=active]:bg-white text-xs sm:text-sm" data-testid="tab-vat">VAT</TabsTrigger>}
              <TabsTrigger value="expenses" className="data-[state=active]:bg-white text-xs sm:text-sm" data-testid="tab-expenses">Expenses</TabsTrigger>
              <TabsTrigger value="balance-sheet" className="data-[state=active]:bg-white text-xs sm:text-sm" data-testid="tab-balance-sheet">Balance Sheet</TabsTrigger>
              <TabsTrigger value="trial-balance" className="data-[state=active]:bg-white text-xs sm:text-sm" data-testid="tab-trial-balance">Trial Balance</TabsTrigger>
              <TabsTrigger value="general-ledger" className="data-[state=active]:bg-white text-xs sm:text-sm" data-testid="tab-general-ledger">General Ledger</TabsTrigger>
            </TabsList>
            {isDetailReport && (
              <div className="flex gap-2 flex-shrink-0 items-center">
                <div className="flex border border-zinc-200 rounded-lg bg-zinc-100/50 p-0.5" data-testid="view-mode-toggle">
                  <button onClick={() => setViewMode('visual')} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${viewMode === 'visual' ? 'bg-white shadow-sm text-zinc-900' : 'text-zinc-500 hover:text-zinc-700'}`} data-testid="view-mode-visual"><LayoutGrid className="w-3.5 h-3.5" />Visual</button>
                  <button onClick={() => setViewMode('list')} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${viewMode === 'list' ? 'bg-white shadow-sm text-zinc-900' : 'text-zinc-500 hover:text-zinc-700'}`} data-testid="view-mode-list"><List className="w-3.5 h-3.5" />List</button>
                </div>
                {(CSV_SUPPORTED.includes(activeReport) || PDF_SUPPORTED.includes(activeReport)) && <div className="h-6 w-px bg-zinc-200" />}
                {CSV_SUPPORTED.includes(activeReport) && <Button variant="outline" size="sm" onClick={() => handleExport('csv')} data-testid="export-csv-btn"><Download className="w-4 h-4 mr-1.5" />CSV</Button>}
                {PDF_SUPPORTED.includes(activeReport) && <Button variant="outline" size="sm" onClick={() => handleExport('pdf')} data-testid="export-pdf-btn"><FileText className="w-4 h-4 mr-1.5" />PDF</Button>}
                {'share' in navigator && <Button variant="outline" size="sm" onClick={handleShare} data-testid="share-report-btn"><Share2 className="w-4 h-4 mr-1.5" />Share</Button>}
              </div>
            )}
          </div>

          {/* Date Filters */}
          {isDetailReport && (
            <Card>
              <CardContent className="py-3">
                <div className="flex flex-wrap gap-3 items-end">
                  {needsAsOfDate && (
                    <>
                      <div className="space-y-1"><Label className="text-xs">As of</Label><Input type="date" value={filters.as_of_date} onChange={e => setFilters(p => ({ ...p, as_of_date: e.target.value }))} className="w-40 h-9" data-testid="filter-as-of-date" /></div>
                      <div className="relative">
                        <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={() => setShowPresets(showPresets === 'asOf' ? false : 'asOf')} data-testid="as-of-presets-btn">
                          <CalendarDays className="w-3.5 h-3.5" />Quick<ChevronDown className="w-3 h-3" />
                        </Button>
                        {showPresets === 'asOf' && (
                          <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-zinc-200 rounded-lg shadow-lg py-1 min-w-[200px]">
                            {getDatePresets().asOf.map(p => (
                              <button key={p.label} onClick={() => { applyAsOfPreset(p); setShowPresets(false); }} className="w-full text-left px-3 py-2 text-sm hover:bg-zinc-50 flex items-center justify-between" data-testid={`preset-${p.label.toLowerCase().replace(/\s+/g, '-')}`}>
                                <span>{p.label}</span>
                                <span className="text-xs text-zinc-400 font-mono">{p.date}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                  {needsDateRange && (
                    <>
                      <div className="space-y-1"><Label className="text-xs">From</Label><Input type="date" value={filters.start_date} onChange={e => setFilters(p => ({ ...p, start_date: e.target.value }))} className="w-40 h-9" data-testid="filter-start-date" /></div>
                      <div className="space-y-1"><Label className="text-xs">To</Label><Input type="date" value={filters.end_date} onChange={e => setFilters(p => ({ ...p, end_date: e.target.value }))} className="w-40 h-9" data-testid="filter-end-date" /></div>
                      <div className="relative">
                        <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={() => setShowPresets(showPresets === 'range' ? false : 'range')} data-testid="date-presets-btn">
                          <CalendarDays className="w-3.5 h-3.5" />Quick<ChevronDown className="w-3 h-3" />
                        </Button>
                        {showPresets === 'range' && (
                          <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-zinc-200 rounded-lg shadow-lg py-1 min-w-[240px]">
                            {getDatePresets().range.map(p => (
                              <button key={p.label} onClick={() => { applyRangePreset(p); setShowPresets(false); }} className="w-full text-left px-3 py-2 text-sm hover:bg-zinc-50 flex items-center justify-between gap-4" data-testid={`preset-${p.label.toLowerCase().replace(/[\s()]/g, '-')}`}>
                                <span>{p.label}</span>
                                <span className="text-xs text-zinc-400 font-mono whitespace-nowrap">{p.start.slice(5)}&ndash;{p.end.slice(5)}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                  <Button onClick={() => loadReport(activeReport)} className="bg-[#D94E18] hover:bg-[#C2410C] h-9" data-testid="apply-filters-btn">Apply</Button>
                  {canShowPY && (
                    <button
                      onClick={() => setIncludePY(!includePY)}
                      className={`flex items-center gap-1.5 px-3 h-9 rounded-md border text-xs font-medium transition-colors ${
                        includePY ? 'bg-[#D94E18]/10 border-[#D94E18]/30 text-[#D94E18]' : 'bg-white border-zinc-200 text-zinc-500 hover:text-zinc-700 hover:border-zinc-300'
                      }`}
                      data-testid="toggle-py-btn"
                    >
                      <History className="w-3.5 h-3.5" />Prior Year
                    </button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Report Content */}
        {loading ? (
          <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#D94E18] border-t-transparent rounded-full animate-spin" /></div>
        ) : activeReport === 'overview' ? (
          <div className="mt-4"><OverviewSection healthData={healthData} integrityData={integrityData} setActiveReport={setActiveReport} /></div>
        ) : activeReport === 'heartbeat' ? (
          <div className="mt-4"><HeartbeatSection heartbeatData={heartbeatData} /></div>
        ) : (
          <div className="mt-4">
            {reportData?.integrity_alert && (
              <div className="mb-4 p-4 bg-red-600 text-white rounded-lg border-2 border-red-700 flex items-start gap-3" data-testid="integrity-alert">
                <AlertCircle className="w-6 h-6 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold text-sm uppercase tracking-wider">Integrity Alert</p>
                  <p className="text-sm mt-1">{reportData.integrity_alert}</p>
                </div>
              </div>
            )}
            {reportData && (
              <ReportingAssistant
                reportType={activeReport}
                reportData={{
                  period: reportData.period,
                  revenue_total: reportData.revenue?.total,
                  expenses_total: reportData.expenses?.total,
                  net_income: reportData.net_income,
                  total_assets: reportData.assets?.total,
                  total_liabilities: reportData.liabilities?.total,
                  top_expenses: reportData.expenses?.accounts?.slice(0, 5)?.map(a => ({ name: a.name, amount: a.balance })),
                }}
              />
            )}
            {renderActiveReport()}
          </div>
        )}
      </Tabs>
    </div>
  );
}
